import api from 'api'
import {
  decamelizeThatDontBreaksFile,
  ObjectToFormDataWithFiles,
} from 'helpers/converters.helper'
import {
  IDc3AssociatedCourse,
  IDc3AssociatedCoursesSummary,
  IDc3Attestation,
  IDc3AttestationDetail,
  IDc3AttestationPayload,
  IDc3AttestationsListParams,
  IDc3AttestationsListResponse,
  IDc3NormativeCoursesListParams,
  IDc3NormativeCoursesListResponse,
} from 'interfaces/dc3Attestation'

const getDc3Attestations = async ({
  page,
  perPage,
  search,
  order,
}: IDc3AttestationsListParams): Promise<IDc3AttestationsListResponse> => {
  const response = await api.get<{
    data: IDc3Attestation[]
    meta: { total: number }
  }>('/administration/dc3_attestations', {
    params: {
      page,
      per_page: perPage,
      search,
      order,
    },
  })

  return {
    data: response.data.data,
    total: response.data.meta.total,
  }
}

const getDc3Attestation = async (
  attestationId: number
): Promise<IDc3AttestationDetail> => {
  const response = await api.get<{ data: IDc3AttestationDetail }>(
    `/administration/dc3_attestations/${attestationId}`
  )

  return response.data.data
}

const getAssociatedCourses = async (
  attestationId: number
): Promise<IDc3AssociatedCourse[]> => {
  const response = await api.get<{ data: IDc3AssociatedCourse[] }>(
    `/administration/dc3_attestations/${attestationId}/courses`
  )

  return response.data.data
}

const searchAssociatedCourses = async (
  attestationId: number,
  { page, search, perPage }: { page: number; search: string; perPage?: number }
): Promise<{ data: IDc3AssociatedCoursesSummary }> => {
  const response = await api.get<IDc3AssociatedCoursesSummary>(
    `/administration/dc3_attestations/${attestationId}/courses`,
    {
      params: {
        page,
        search,
        per_page: perPage,
      },
    }
  )

  return { data: response.data }
}

const getAvailableNormativeCourses = async ({
  page,
  perPage,
  search,
  excludeCourseIds,
}: IDc3NormativeCoursesListParams): Promise<IDc3NormativeCoursesListResponse> => {
  const response = await api.get<{
    data: IDc3AssociatedCourse[]
    meta: { total: number }
  }>('/administration/dc3_attestations/available_normative_courses', {
    params: {
      page,
      per_page: perPage,
      search,
      ...(excludeCourseIds?.length
        ? { exclude_course_ids: excludeCourseIds }
        : {}),
    },
  })

  return {
    data: response.data.data,
    total: response.data.meta.total,
  }
}

const syncAssociatedCourses = async (
  attestationId: number,
  courseIds: number[]
): Promise<void> => {
  await api.put(`/administration/dc3_attestations/${attestationId}/courses`, {
    course_ids: courseIds,
  })
}

const destroyDc3Attestation = async (attestationId: number): Promise<void> => {
  await api.delete(`/administration/dc3_attestations/${attestationId}`)
}

const buildFormData = (payload: IDc3AttestationPayload): FormData => {
  const decamelizeData = decamelizeThatDontBreaksFile(payload)
  return ObjectToFormDataWithFiles(decamelizeData, 'dc3_attestation')
}

const sanitizeUpdatePayload = (
  payload: Partial<IDc3AttestationPayload>
): Partial<IDc3AttestationPayload> => {
  const sanitized: Partial<IDc3AttestationPayload> = { ...payload }

  ;(
    [
      'instructorSignature',
      'legalRepresentativeSignature',
      'workersRepresentativeSignature',
    ] as const
  ).forEach((key) => {
    if (!(sanitized[key] instanceof File)) {
      delete sanitized[key]
    }
  })

  return sanitized
}

const createDc3Attestation = async (
  payload: IDc3AttestationPayload
): Promise<IDc3AttestationDetail> => {
  const response = await api.post<{ data: IDc3AttestationDetail }>(
    '/administration/dc3_attestations',
    buildFormData(payload),
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  )

  return response.data.data
}

const updateDc3Attestation = async (
  attestationId: number,
  payload: Partial<IDc3AttestationPayload>
): Promise<IDc3AttestationDetail> => {
  const sanitizedPayload = sanitizeUpdatePayload(payload)
  const response = await api.patch<{ data: IDc3AttestationDetail }>(
    `/administration/dc3_attestations/${attestationId}`,
    buildFormData(sanitizedPayload as IDc3AttestationPayload),
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  )

  return response.data.data
}

const duplicateDc3Attestation = async (
  attestationId: number
): Promise<IDc3AttestationDetail> => {
  const response = await api.post<{ data: IDc3AttestationDetail }>(
    `/administration/dc3_attestations/${attestationId}/duplicate`
  )

  return response.data.data
}

export const isDc3AttestationDuplicateNameError = (error: unknown): boolean => {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: { name?: unknown } } }
    }
  )?.response

  if (response?.status !== 422) return false

  const nameErrors = response.data?.message?.name
  return Array.isArray(nameErrors) && nameErrors.length > 0
}

export const dc3AttestationsService = {
  getDc3Attestations,
  getDc3Attestation,
  getAssociatedCourses,
  searchAssociatedCourses,
  getAvailableNormativeCourses,
  syncAssociatedCourses,
  createDc3Attestation,
  updateDc3Attestation,
  destroyDc3Attestation,
  duplicateDc3Attestation,
}
